/**
 * Public B2B marketing: /organizations
 * Signed-in users are sent to /account-admin (single admin surface).
 */

import { useCallback, useContext } from 'react'
import { View, Platform } from 'react-native'
import { Redirect, useRouter, type Href } from 'expo-router'
import { useSupabaseAuth } from '../src/shared/contexts/SupabaseAuthContext'
import { LanguageContext } from '../src/i18n'
import { SignInLoadingScreen } from '../components/signIn'
import { LandingOrgAdminNewNav, LandingHeaderControls } from '../components/landing'
import { AccountAdminPublicMarketing } from '../src/accounts/components/AccountAdminPublicMarketing/AccountAdminPublicMarketing'
import { useAccountAdminDrawerShift } from '../src/accounts/contexts/AccountAdminDrawerShiftContext'
import { getWebAccountAdminDrawerPushStyle } from '../src/accounts/helpers/accountAdminDrawerPushWeb.helper'
import { accountAdminSiteShellStyles } from '../appStyles/_accountAdmin.styles'
import {
  appendReturnUrlQuery,
  buildPublicSignInHref,
} from '../src/signIn/helpers/postAuthRedirect.helper'
import { PUBLIC_SIGN_IN_PATH } from '../src/signIn/constants/publicAuthRoutes.constants'
import {
  LANDING_SITE_NAV_ROUTES,
  LANDING_SITE_NAV_SCROLL_OFFSET_PX,
} from '../src/landing/constants/landingSiteNav.constants'
import { scrollWebWindowToElement } from '../src/landing/helpers/scrollWebMarketingSection.helper'

/** After auth from marketing CTAs, land in organization admin. */
const RETURN_TO_ADMIN = '/account-admin'

export default function OrganizationsMarketingRoute() {
  const { isLoaded, isSignedIn } = useSupabaseAuth()
  const router = useRouter()
  const languageContext = useContext(LanguageContext)
  const { shiftPx } = useAccountAdminDrawerShift()

  const scheduleScrollToLandingSection = useCallback((elementId: string) => {
    if (Platform.OS !== 'web' || typeof document === 'undefined') return
    window.setTimeout(() => {
      scrollWebWindowToElement(
        document.getElementById(elementId),
        LANDING_SITE_NAV_SCROLL_OFFSET_PX,
      )
    }, 380)
  }, [])

  const onPressHome = useCallback(() => {
    router.push(LANDING_SITE_NAV_ROUTES.HOME as Href)
  }, [router])

  const onPressContact = useCallback(() => {
    router.push(LANDING_SITE_NAV_ROUTES.CONTACT as Href)
  }, [router])

  const onPressFeatures = useCallback(() => {
    void router.push(LANDING_SITE_NAV_ROUTES.HOME as Href)
    scheduleScrollToLandingSection('features')
  }, [router, scheduleScrollToLandingSection])

  const onPressPricing = useCallback(() => {
    void router.push(LANDING_SITE_NAV_ROUTES.HOME as Href)
    scheduleScrollToLandingSection('pricing')
  }, [router, scheduleScrollToLandingSection])

  if (!isLoaded) {
    return <SignInLoadingScreen />
  }

  if (isSignedIn) {
    return <Redirect href="/account-admin" />
  }

  if (!languageContext) {
    throw new Error('OrganizationsMarketingRoute must be used within LanguageProvider')
  }

  const signInHref = appendReturnUrlQuery(PUBLIC_SIGN_IN_PATH, RETURN_TO_ADMIN)
  const signUpHref = buildPublicSignInHref({ signup: true, returnUrl: RETURN_TO_ADMIN })

  return (
    <View
      style={[
        accountAdminSiteShellStyles.shell,
        getWebAccountAdminDrawerPushStyle(shiftPx),
      ]}
    >
      <AccountAdminPublicMarketing
        signInHref={signInHref}
        signUpHref={signUpHref}
        headerSlot={
          <LandingOrgAdminNewNav
            onPressHome={onPressHome}
            onPressContact={onPressContact}
            onPressFeatures={onPressFeatures}
            onPressPricing={onPressPricing}
            headerRight={<LandingHeaderControls />}
          />
        }
      />
    </View>
  )
}
